import React from 'react';
import {DoppePaymentElement} from '../../../components/doppe-payment-element/doppe-payment-element';
import {useDoppeViewerClientServices} from '../../../client/doppe-user-app/doppe-viewer-client-services';
import {
    BymoPageViewerMode,
    useBymoPageViewerContext,
} from '../../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import {IDoppeViewerActionContext} from '../../../doppe-sdk';
import {ObservableValue, Price} from '@wix/devzai-utils-common';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {DoppeDtoCheckoutIntent} from '../../../client-server-common/types/doppe-dto-checkout-intent';
import {
    useObservableValue,
    useEventCallback, reactCreateMemo } from '@wix/devzai-utils-react';
import {doppeHideableValueGetVisibleValue} from '../../../client-server-common';


export type DoppeActionCheckoutViewSettings = {
    checkoutIntent: DoppeDtoCheckoutIntent;
    checkoutTitle: string;
};

export const DoppeActionCheckoutView = reactCreateMemo(function DoppeActionCheckoutView(props: DoppeActionCheckoutView.Props) {
    const {
        observableCheckoutDetails,
        previewCheckoutButtonText,
        onDone,
        createStripePaymentIntent,
        saveCheckoutIntent,
        demoMode,
        actionContext,
        isCustomAmount,
    } = props;


    const {
        checkoutIntent,
    } = useObservableValue(observableCheckoutDetails);

    const {
        checkoutSettings,
        price,
    } = checkoutIntent;


    const {
        showAddressInput,
        showPhoneInput,
        showConsent,
        showConsentCheckbox,
        customConsentText,
    } = checkoutSettings

    const clientServices = useDoppeViewerClientServices();
    const pageViewerContext = useBymoPageViewerContext();
    const doppeViewerLocales = useDoppeViewerLocales();

    const logBiSubmitPaymentIntent = useEventCallback(() => {
        return (invalidFields?: string[]) => {
            if (pageViewerContext.mode === BymoPageViewerMode.Live) {
                clientServices.viewerAnalyticsClient.logEvent('uouSubmitPaymentIntent', {
                    ...actionContext.getBiIdFields(),
                    isCustomAmount,
                    value: `${price.amount}`,
                    currency: price.currencyCode,
                    invalidFields: invalidFields?.join(',') || '',
                    innerPageId: actionContext.getCurrentInnerPageId(),
                });
            }
        };
    });

    const logBiPaymentConfirmation = (options: { price: Price; error?: string }) => {
        const {
            price,
            error,
        } = options;
        if (pageViewerContext.mode === BymoPageViewerMode.Live) {
            clientServices.viewerAnalyticsClient.logEvent('uouPaymentConfirmation', {
                ...actionContext.getBiIdFields(),
                value: `${price.amount}`,
                currency: price.currencyCode,
                error: error ?? '',
                innerPageId: actionContext.getCurrentInnerPageId(),
            });
        }
    };

    const [errorMessage, setErrorMessage] = React.useState<string>();

    return !errorMessage ? (
        <DoppePaymentElement
            previewCheckoutButtonText={previewCheckoutButtonText}
            showAddressInput={showAddressInput}
            showPhoneInput={showPhoneInput}
            showEmailInput={true}
            returnUrl={typeof window !== 'undefined' ? window.location.href : ''}
            onError={(error) => {
                logBiPaymentConfirmation({
                    price: price,
                    error: error.type,
                });
                console.log('error', error);
                setErrorMessage(doppeViewerLocales['paymentError']);
            }}
            onDone={(args) => {
                logBiPaymentConfirmation({ price: price });
                onDone(args);
            }}
            onSubmitPaymentClick={logBiSubmitPaymentIntent}
            price={price}
            createStripePaymentIntent={createStripePaymentIntent}
            saveCheckoutIntent={saveCheckoutIntent}
            demoMode={demoMode}
            showConsent={showConsent}
            consentText={doppeHideableValueGetVisibleValue(customConsentText, '')}
            showConsentCheckbox={showConsentCheckbox}
        />
    ) : (
        <div>{errorMessage}</div>
    );
});

export namespace DoppeActionCheckoutView {
    export interface Props {
        observableCheckoutDetails: ObservableValue<DoppeActionCheckoutViewSettings>;
        previewCheckoutButtonText?: string;
        onDone: ({
            name,
            paymentIntentId,
            consent,
        }: {
            name: string;
            message?: string;
            paymentIntentId: string;
            consent: boolean;
        }) => void;
        actionContext: IDoppeViewerActionContext<any>;
        createStripePaymentIntent: () => Promise<{ clientSecret: string; paymentIntentId: string }>;
        saveCheckoutIntent: (options: {
            uouCheckoutData: DoppeDtoCheckoutIntent.UouCheckoutData;
            paymentIntentId: string;
        }) => Promise<void>;
        demoMode: boolean;
        isCustomAmount: boolean;
    }
}
